import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import ContentBottom from '../../components/ContentBottom';
import Header from '../../components/Common/Header/Header';
import ContentApp from '../../components/ContentApp';
import useForm from '../../helpers/useForm';
import ErrorBox from '../../components/Common/ErrorBox/ErrorBox';
import InputFormBorder from '../../components/Common/InputFormBorder/InputFormBorder';
import { useDispatch } from 'react-redux';
import Button from '../../components/Common/Button/Button';
import { setCanRefresh } from '../../actions/appActions';
import GeneralSelectorPicker from '../../components/SelectorPicker/GeneralSelectorPicker';
import { WelletContext } from '../../context/WelletContext';
import { getStripeWithdrawalSetup } from '../../services/withdrawal/withdrawalService';
import SpinnerLoader from '../../components/Common/Spinner/SpinnerLoader';
import { Spinner } from '../../components/Common/Spinner/Spinner';
import { CircleSpinner } from 'react-spinners-kit';

const PersonalInformationForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSocial, setSelectedSocial] = useState({
    value: 'instagram',
    label: 'Instagram',
    prefix: 'https://www.instagram.com/',
  });
  const [selectedCountry, setSelectedCountry] = useState({ name: t('profile.nationality') });
  const [withdrawalSetup, setWithdrawalSetup] = useState(null);

  function validate(values) {
    let errors = {};

    if (!values.countryCode) {
      errors.countryCode = t('selectCountry');
    }

    if (!values.address) {
      errors.address = t('address.requieredText');
    }
    if (!values.postalCode) {
      errors.postalCode = t('postalCode.requieredText');
    }

    if (!values.city) {
      errors.city = t('city.requieredText');
    }

    if (!values.state) {
      errors.state = t('state.requieredText');
    }

    if (!values.urlProfile) {
      errors.urlProfile = t('profileURL.' + selectedSocial.value + '.requieredText');
    } else {
      const url = validateUrl(values.urlProfile);
      handleChange(null, 'url', url);

      const pattern = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,63}(\/[^\s]*)?$/;

      if (!pattern.test(url)) {
        errors.urlProfile = t('profileURL.' + selectedSocial.value + '.validUrl');
      }
    }

    return errors;
  }

  const sendForm = async () => {
    history.push({
      pathname: `/add-account`,
      state: { values },
    });
  };

  let { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);

  useEffect(() => {
    dispatch(setCanRefresh(false));

    return () => {
      dispatch(setCanRefresh(true));
    };
  }, []);

  const social = [
    { value: 'instagram', label: 'Instagram', prefix: 'https://www.instagram.com/' },
    { value: 'facebook', label: 'Facebook', prefix: 'https://www.facebook.com/' },
    { value: 'linkedin', label: 'Linkedin', prefix: 'https://www.linkedin.com/' },
    { value: 'other', label: t('industry.other'), prefix: '' },
  ];

  const handleSocialChange = (selected) => {
    setSelectedSocial(selected);
  };

  const validateUrl = (url) => {
    switch (selectedSocial.value) {
      case 'instagram':
        let result = '';
        let auxUrl = url.replaceAll(selectedSocial.prefix, '');
        auxUrl = auxUrl.replaceAll('https://', '');
        auxUrl = auxUrl.replaceAll('@', '');
        auxUrl = auxUrl.trim();
        auxUrl = auxUrl.split('/');

        if (auxUrl) result = auxUrl[0];

        return selectedSocial.prefix + result;
      case 'facebook':
        let resultFb = '';
        let auxUrlFb = url.replaceAll(selectedSocial.prefix, '');
        auxUrlFb = auxUrlFb.replaceAll('https://', '');
        auxUrlFb = auxUrlFb.replaceAll('@', '');
        auxUrlFb = auxUrlFb.trim();
        auxUrlFb = auxUrlFb.split('/');

        if (auxUrlFb) resultFb = auxUrlFb[0];

        return selectedSocial.prefix + resultFb;
      default:
        return url;
    }
  };

  const countries = [
    {
      value: 'AR',
      label: t('Argentina'),
    },
    {
      value: 'DO',
      label: t('dominicanRepublic'),
    },
    {
      value: 'MX',
      label: 'Mexico',
    },
    {
      value: 'ES',
      label: t('spain'),
    },
    {
      value: 'US',
      label: t('usa'),
    },
    {
      value: 'CO',
      label: t('Colombia'),
    },
  ];

  const handleChangeCountry = (selectedOption) => {
    handleChange({
      target: { name: 'countryCode', value: selectedOption.value },
      persist: () => {},
    });
    setSelectedCountry(selectedOption);
  };

  const getStripeSetup = async () => {
    try {
      const response = await getStripeWithdrawalSetup(welletContext);
      setWithdrawalSetup(response);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStripeSetup();
  }, []);

  if (loading) {
    return (
      <div className='loading-app'>
        <div className='loaderDiv'>
          <CircleSpinner
            className='spinner-app mx-auto'
            style={{ margin: 'auto' }}
            size={40}
            color='var(--color-font-primary)'
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <ContentApp>
        <div className='fw-bold font-medium mt-4'>{t('stripe.addPersonalInformation')}</div>
        <div className='text-small text-primary fw-semibold mt-5'>{t('personalInformation')}</div>
        <div className='mt-3'>
          <GeneralSelectorPicker
            options={countries.filter((country) =>
              withdrawalSetup.supportedCountries.includes(country.value),
            ).sort((a, b) => a.label.localeCompare(b.label))}
            selectedValue={selectedCountry}
            onChange={handleChangeCountry}
            placeholder={t('stripe.countryOfResidence')}
          />
          {errors.countryCode && (
            <div className='error-label text-center' style={{ position: 'relative' }}>
              {errors.countryCode}
            </div>
          )}
        </div>
        <div className='mt-3'>
          <InputFormBorder
            color='#757575'
            name='address'
            type='text'
            onChange={handleChange}
            value={values.address}
            error={errors.address}
            placeholder={t('address.label')}
            border={'none'}
          />
        </div>

        <div className='mt-3'>
          <InputFormBorder
            color='#757575'
            name='postalCode'
            type='text'
            onChange={handleChange}
            value={values.postalCode}
            error={errors.postalCode}
            placeholder={t('postalCode.label')}
            border={'none'}
          />
        </div>

        <div className='mt-3'>
          <InputFormBorder
            color='#757575'
            name='city'
            type='text'
            onChange={handleChange}
            value={values.city}
            error={errors.city}
            placeholder={t('city.label')}
            border={'none'}
          />
        </div>

        <div className='mt-3'>
          <InputFormBorder
            color='#757575'
            name='state'
            type='text'
            onChange={handleChange}
            value={values.state}
            error={errors.state}
            placeholder={t('state.label')}
            border={'none'}
          />
        </div>

        <div className='text-small text-primary fw-semibold mt-5'>{t('digitalProfile')}</div>
        <div className='my-3'>
          <GeneralSelectorPicker
            onChange={handleSocialChange}
            selectedValue={{ value: selectedSocial.value }}
            options={social}
            placeholder='Instagram'
          />
        </div>
        <InputFormBorder
          color='#757575'
          name='urlProfile'
          type='text'
          onChange={handleChange}
          value={values.urlProfile}
          error={errors.urlProfile}
          placeholder={t('profileURL.' + selectedSocial.value + '.label')}
          border={'none'}
        />
      </ContentApp>
      <ContentBottom>
        <Button
          text={t('continue')}
          size='large'
          icon
          typeIcon='arrow'
          color='#000'
          className='btn-primary'
          iconColor='#000'
          type='button'
          onClick={() => handleSubmit()}
        />
      </ContentBottom>
    </>
  );
};

export default PersonalInformationForm;
